import React, { useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import { ContentViewer } from '@emerald-works-nova/content-viewer'
import makeStyles from '@mui/styles/makeStyles'
import { useSession } from '@emerald-works/react-auth'
import { useTenant } from '@emerald-works-nova/auth'
import consts from '../../../consts'
import dompurify from 'dompurify'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useAlert } from '../../../hooks'
import useGoogleAdsense from './../../../hooks/useGoogleAdsense'
import { GoogleAd, LegacyGoogleAd } from '../..'

// Make the empty body take up space which avoids rendering the recommended
// directly under the article header if they load before the body.
const useStyles = makeStyles(({ breakpoints }) => ({
  empty: {
    minHeight: '100vh'
  },
  loaded: {
    minHeight: 0
  }
}))

const Body = ({ className, body }) => {
  // PENG-2099 - Remove if Success
  const [searchParams] = useSearchParams()
  const isAdsTest = searchParams.has('adsTest')
  const adsTestValue = searchParams.get('adsTest')

  const classes = useStyles()
  const bodyRef = useRef()
  const { isAuthenticated } = useSession()
  const tenant = useTenant()
  const isClubTenant = tenant.pk === consts.DEFAULT_CLUB_PK
  const { enqueueMsg } = useAlert()
  const { useAdsense } = useGoogleAdsense()

  const [getScormLaunchLink] = useEvent([{
    eventName: 'scormGetAccessLink',
    onSuccess: (payload) => {
      window.open(payload.launchLink, '_blank', 'fullscreen=yes, location=yes, scrollbars=yes,status=yes')
    },
    onError: (err) => {
      console.error(err)
      enqueueMsg('An unexpected error happened, please try again later', 'error')
    }
  }])

  const isHTML = body.data?.html?.length && (!body.data?.blocks || !body.data?.entityMap)

  const handleStartScorm = ({ courseId }) => {
    getScormLaunchLink.trigger({ courseId })
  }

  const renderViewer = () => {
    if (isHTML) {
      const sanitizedHtml = dompurify.sanitize(body.data.html, {
        USE_PROFILES: {
          html: true,
          svg: true,
          svgFilters: true
        },
        FORCE_BODY: true,
        ADD_TAGS: ['iframe', 'style'],
        ADD_ATTR: ['target', 'style', 'class']
      })

      return (
        <div
          data-test='article-body'
          className={clsx(className)}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      )
    }

    return (
      <ContentViewer
        body={body}
        isAuthenticated={isAuthenticated}
        onStartScorm={handleStartScorm}
        isLoadingScormLink={getScormLaunchLink.isWorking}
        isClub={isClubTenant}
        GoogleAdsComponent={({ adKey }) =>
          useAdsense && (
            isAdsTest
              ? <GoogleAd
                  adKey={adKey}
                  adPosition='content-body'
                  adSource='components>article>body>index.js'
                  fromContent
                  adSolution={adsTestValue}
                />
              : <LegacyGoogleAd location='content-body' />
          )}
        className={clsx(className)}
        onCalculateQuizResult={({ quiz, classification, score }) => {
          console.log('Quiz result on toolkit', quiz, classification, score)
        }}
        data-test='article-body'
      />
    )
  }

  return (
    <div
      ref={bodyRef}
      data-test='article-body-container'
      className={
        bodyRef.current?.hasChildNodes() ? classes.loaded : classes.empty
      }
    >
      {renderViewer()}
    </div>
  )
}

export default Body
